import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { Column } from "@/components/ui/DataTable";
import {
  useGetRestaurantCustomersQuery,
  useLazyExportRestaurantCustomersQuery,
} from "@/redux/services/customer.api";
import { RestaurantCustomer } from "@/redux/services/customer.type";
import { useAppSelector } from "@/redux/features/hooks";
import {
  formatCompactPrice,
  formatDate,
  formatPhoneNumber,
  formatRelativeDate,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import toast from "react-hot-toast";
import PageTitle from "@/components/PageTitle";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import DashLayout from "@/layouts/DashLayout";
import { Roles } from "@/redux/services/user/user.type";
import { logger } from "@/services/Logger";
import { useDebounce } from "@/hooks/useDebounce";
import { Loader } from "@/components/Spinner";
import {
  DEFAULT_CUSTOMER_SORT,
  SortOrder,
  buildCustomerListParams,
} from "@/lib/customers";

export default function CustomersList() {
  const navigate = useNavigate();
  const { currentRestaurant, currentUser } = useAppSelector(
    (state) => state.user
  );
  const currency = useRestaurantCurrency();
  const { canManage } = usePermissions();
  // CSV export is gated on customers.manage (managers/owners hold it; delegated
  // staff need it granted).
  const canExportCustomers = canManage("customers");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    DEFAULT_CUSTOMER_SORT.order
  );
  const [sortColumn, setSortColumn] = useState<string>(
    DEFAULT_CUSTOMER_SORT.column
  );
  const [isExporting, setIsExporting] = useState(false);

  // Debounce search query to avoid too many API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data, isLoading } = useGetRestaurantCustomersQuery(
    {
      restaurantId: currentRestaurant?.id || "",
      ...buildCustomerListParams({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchQuery,
        sortColumn,
        sortOrder,
      }),
    },
    {
      skip: !currentRestaurant?.id,
    }
  );

  const [exportCustomers] = useLazyExportRestaurantCustomersQuery();

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((column: string, order: SortOrder) => {
    setSortColumn(column);
    setSortOrder(order);
    setCurrentPage(1);
  }, []);

  const handleViewCustomer = useCallback(
    (customer: RestaurantCustomer) => {
      navigate(`/customers/${customer.customer.id}`);
    },
    [navigate]
  );

  const handleExport = useCallback(async () => {
    if (!currentRestaurant?.id || !canExportCustomers || isExporting) return;

    setIsExporting(true);
    try {
      const result = await exportCustomers(currentRestaurant.id).unwrap();

      // Create a download link
      const url = window.URL.createObjectURL(result);
      const link = document.createElement("a");
      link.href = url;
      link.download = `customers_${formatDate(new Date())}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Export réussi!");
    } catch (error) {
      logger.error(
        "CustomersList: handleExport",
        "Error exporting customers",
        error
      );
      toast.error("Erreur lors de l'export");
    } finally {
      setIsExporting(false);
    }
  }, [currentRestaurant?.id, exportCustomers, canExportCustomers, isExporting]);

  const columns: Column[] = [
    {
      key: "name",
      label: "Nom",
      sortable: true,
      type: "text",
      formatter: (_, row: RestaurantCustomer) => (
        <span className="font-medium">
          {row.customer.fullName ?? "Pas défini"}
        </span>
      ),
    },
    {
      key: "phone",
      label: "Téléphone",
      type: "text",
      nowrap: true,
      formatter: (_, row: RestaurantCustomer) =>
        formatPhoneNumber(row.customer.phone),
    },
    {
      key: "totalOrders",
      label: "Nb. de commandes",
      sortable: true,
      type: "number",
    },
    {
      key: "totalSpent",
      label: "Total dépensé",
      sortable: true,
      nowrap: true,
      formatter: (_, row: RestaurantCustomer) => (
        <span className="font-medium">
          {formatCompactPrice(row.totalSpent, currency)}
        </span>
      ),
    },
    {
      key: "firstOrderAt",
      label: "1ère commande",
      sortable: true,
      type: "date",
      nowrap: true,
      formatter: (_, row: RestaurantCustomer) =>
        row.firstOrderAt
          ? formatDate(new Date(row.firstOrderAt), false, true)
          : "-",
    },
    {
      key: "lastOrderAt",
      label: "Dernière visite",
      sortable: true,
      type: "date",
      nowrap: true,
      formatter: (_, row: RestaurantCustomer) =>
        row.lastOrderAt ? formatRelativeDate(new Date(row.lastOrderAt)) : "-",
    },
  ];

  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const needsRestaurantSelection = isOwner && !currentRestaurant?.id;

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.cashier, Roles.manager, Roles.owner]}
        allowedPermissions={["customers.view"]}
      >
        {needsRestaurantSelection ? (
          <div className="space-y-6">
            <PageTitle
              title="Liste des clients"
              description="Gérez vos clients et consultez leur historique"
            />
            <div className="bg-card rounded-lg border border-border p-6 flex items-center justify-center h-64 text-muted-foreground">
              <p>Sélectionnez un restaurant pour voir les clients</p>
            </div>
          </div>
        ) : (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <PageTitle
              title="Liste des clients"
              description="Gérez vos clients et consultez leur historique"
            />
            {canExportCustomers && (
              <button
                onClick={handleExport}
                disabled={!currentRestaurant?.id || isExporting}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              >
                {isExporting && <Loader border="border-white" />}
                {isExporting ? "Export en cours..." : "Exporter CSV"}
              </button>
            )}
          </div>

          {!currentRestaurant ? (
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="text-center bg-yellow-50 border-l-4 border-yellow-800 rounded py-4">
                Veuillez sélectionner un restaurant
              </div>
            </div>
          ) : (
            <DataTable
              data={data?.results || []}
              columns={columns}
              loading={isLoading}
              totalCount={data?.count || 0}
              currentPage={currentPage}
              pageSize={pageSize}
              sortBy={sortColumn}
              sortOrder={sortOrder}
              searchValue={searchQuery}
              onSearch={handleSearch}
              onSort={handleSort}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onRowClick={handleViewCustomer}
              emptyMessage="Aucun client trouvé"
            />
          )}
        </div>
        )}
      </RoleProtectedPage>
    </DashLayout>
  );
}
